/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useContext, useMemo, useState } from 'react';
import { Modal, Input, Typography, DatePicker, Select, Tag, Form } from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import moment from 'moment';
import isEmail from 'validator/es/lib/isEmail';
import { DatePickerProps } from 'antd/lib/date-picker';
import { uniq } from 'lodash';
import { SelectProps } from 'antd/lib/select';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFile, DbFileVersion } from '~src/types/db';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { fadeInKeyframes } from '~src/styles/animations';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { useDoShareFiles } from '~src/hooks/actions/useDoShareFiles';
import { __DEV__ } from '~src/config';

const hours = [...new Array(24)].map((v, index) => index);
/** @returns moment(tomorrow at 17:30) */
const getDefaultDateVal = () =>
  moment().add(14, 'day').set('hours', 23).set('minutes', 59).set('seconds', 0);

const getDefaultEmails = () => (__DEV__ ? ['test@mail.com'] : []);

export interface ShareModalProps extends Partial<ModalProps> {
  files: DbFile[];
  onDone?: () => void;
}
export const ShareModal: React.FC<ShareModalProps> = ({
  files,
  onCancel,
  onDone,
  ...modalProps
}) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const [descriptionVal, setDescriptionVal] = useState('');
  const [dateVal, setDateVal] = useState(getDefaultDateVal);
  const [emails, setEmails] = useState<string[]>(getDefaultEmails);
  const [disableButton, setDisableButton] = useState(false);
  const { doShareFiles } = useDoShareFiles();

  // throw if shown without files
  if (modalProps.visible && !files.length)
    throw new Error('ShareModal should be shown with files!');

  /** make sure date within sensible range */
  const handleDateSelect: DatePickerProps['onSelect'] = (momentDate) => {
    /** next o'clock */
    const minDate = moment().add(1, 'hour').set('minutes', 0).set('seconds', 0);

    if (momentDate < minDate) {
      // set min date
      return setDateVal(minDate);
    }

    setDateVal(momentDate);
  };

  const invalidEmails = useMemo(() => emails.filter((val) => !isEmail(val)), [
    emails,
  ]);

  const isValid = () => {
    if (!emails.length || invalidEmails.length || !files.length) return false;

    return true;
  };

  const handleSubmit = async () => {
    if (!isValid()) return;

    setDisableButton(true);

    const selectedVersions = files
      .map((file) => {
        if (!file.versions?.length) return undefined;
        return fileVersionsContext[file.id] || file.versions[0];
      })
      .filter(Boolean) as DbFileVersion[];

    doShareFiles({
      shareData: {
        description: descriptionVal,
        expires_at: dateVal.unix(),
        is_protected: false,
      },
      fileIds: selectedVersions.map((v) => v.id),
      emails,
    })
      .then(() => onDone && onDone())
      .catch((err) => setDisableButton(false));
  };

  // use these in DatePicker's disabled cells logic
  // create once per render rather than on every function call
  const startOfDay = moment().startOf('day');
  const endOfDay = moment().endOf('day');
  const nowHour = moment().get('hours');

  const tagRender: SelectProps<string[]>['tagRender'] = (tagProps) => {
    const invalid = invalidEmails.includes(tagProps.value as string);
    return (
      <Tag
        css={styles.tag}
        color={invalid ? 'red' : undefined}
        title={invalid ? 'Invalid' : undefined}
        {...tagProps}>
        {tagProps.value}
      </Tag>
    );
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'send-share-link' })}
          icon={['far', 'share-square']}
        />
      }
      okText={intl.formatMessage({ id: 'send' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{
        disabled: !isValid() || disableButton,
      }}
      onOk={handleSubmit}
      onCancel={onCancel}
      afterClose={() => {
        // reset state
        setDisableButton(false);
        setDescriptionVal('');
        setDateVal(getDefaultDateVal);
        setEmails(getDefaultEmails);
      }}
      {...modalProps}>
      <Typography.Paragraph css={styles.label} strong>
        {intl.formatMessage({ id: 'send-link-to' })}
      </Typography.Paragraph>
      <Select<string[]>
        mode="tags"
        style={{ width: '100%' }}
        placeholder={intl.formatMessage({ id: 'share-emails-placeholder' })}
        open={false}
        tokenSeparators={[',', ' ']}
        value={emails}
        onChange={(val) => setEmails(uniq(val))}
        tagRender={tagRender}></Select>
      <Typography.Paragraph css={styles.label} strong>
        {intl.formatMessage({ id: 'share-description-label' })}
      </Typography.Paragraph>
      <Input.TextArea
        css={{ marginBottom: 16 }}
        value={descriptionVal}
        onChange={(e) => setDescriptionVal(e.target.value)}
      />

      <Typography.Paragraph css={styles.label} strong>
        {intl.formatMessage({ id: 'link-valid-until' })}
      </Typography.Paragraph>
      <DatePicker
        css={{
          minWidth: '50%',
          marginBottom: 16,
        }}
        showTime
        showNow={false}
        format="DD-MM-YYYY, HH:mm"
        // disable days before today
        disabledDate={(momentDate) => !!momentDate && momentDate < startOfDay}
        // if today selected, disable past hours
        disabledHours={() =>
          dateVal < endOfDay ? hours.filter((hour) => hour <= nowHour) : []
        }
        value={dateVal}
        onSelect={handleDateSelect}
      />
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({
  label: {
    '&.ant-typography': {
      marginBottom: 5,
    },
  },
  tag: {
    borderRadius: 4,
    marginRight: 3,
    fontSize: 14,
    animation: `${fadeInKeyframes} 0.2s ease-out`,
  },
});
